import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';

// const ASSESSMENT_DURATION_SECONDS = 30 * 60;
const ASSESSMENT_DURATION_SECONDS = 15 * 60;
const MAX_TAB_SWITCHES = 3;

function formatTimeLeft(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function CandidateAssessment() {
  // No react-router-dom in this project - pull the token straight from the URL.
  // Expects the URL to look like: https://yourapp.com/assessment/<token>
  const token = window.location.pathname.split('/').filter(Boolean).pop();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [candidateName, setCandidateName] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // { [questionId]: answerText }
  const [tabSwitchDetected, setTabSwitchDetected] = useState(false);
    const [showTabWarning, setShowTabWarning] = useState(false);
    const [tabSwitchCount, setTabSwitchCount] = useState(0);
const switchCountRef = useRef(0);
  const autoSubmittedRef = useRef(false);
  const answersRef = useRef(answers);
  const questionsRef = useRef(questions);

  useEffect(() => { answersRef.current = answers; }, [answers]);
  useEffect(() => { questionsRef.current = questions; }, [questions]);

  const [timeLeftSeconds, setTimeLeftSeconds] = useState(ASSESSMENT_DURATION_SECONDS);
  const [timeExpired, setTimeExpired] = useState(false);
  const timerStartedRef = useRef(false);


  useEffect(() => {
    fetchAssessment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    // Keeps the browser's camera/mic indicator light on as a deterrent.
    // Stream is never recorded, read, or sent anywhere.
    navigator.mediaDevices?.getUserMedia?.({ video: true, audio: true }).catch(() => {});
  }, []);

useEffect(() => {
  // Ignore the first few seconds so the camera permission prompt
  // (which steals focus) doesn't trigger a false warning.
  let armed = false;
  const armTimer = setTimeout(() => { armed = true; }, 3000);

  // One tab switch fires both "blur" and "visibilitychange".
  // `away` makes sure it only counts once until the candidate comes back.
  let away = false;

  const registerSwitch = () => {
    if (!armed || submitted || questionsRef.current.length === 0) return;
    if (away) return;
    away = true;

    switchCountRef.current += 1;
    const count = switchCountRef.current;
    setTabSwitchCount(count);

    // Only show a warning - never auto-submit
    setShowTabWarning(true);
  };

  const onReturn = () => { away = false; };

  const onVisibilityChange = () => {
    if (document.hidden) registerSwitch();
    else onReturn();
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('blur', registerSwitch);
  window.addEventListener('focus', onReturn);

  return () => {
    clearTimeout(armTimer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('blur', registerSwitch);
    window.removeEventListener('focus', onReturn);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [submitted]);


  useEffect(() => {
    if (!timerStartedRef.current || submitted) return;

    const intervalId = setInterval(() => {
      setTimeLeftSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId);
          if (!autoSubmittedRef.current) {
            autoSubmittedRef.current = true;
            setTimeExpired(true);
            handleSubmit(true);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, submitted]);

  const fetchAssessment = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/assessment/${token}`);
      setCandidateName(res.data.candidateName || '');
      setQuestions(res.data.questions || []);
      if ((res.data.questions || []).length > 0) {
        timerStartedRef.current = true;
      }
    } catch (err) {
      const message = err.response?.data?.message || 'This assessment link is invalid or has expired.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const unansweredCount = questions.filter((q) => !answers[q.id]?.trim()).length;

  const handleSubmit = async (auto = false, violation = false) => {
    if (submitting) return;

    if (!auto) {
      if (unansweredCount > 0) {
        const proceed = window.confirm(
          `You have ${unansweredCount} unanswered question(s). Submit anyway? This cannot be undone.`
        );
        if (!proceed) return;
      } else {
        const proceed = window.confirm('Submit your assessment? You will not be able to make changes after this.');
        if (!proceed) return;
      }
    }

    const currentAnswers = auto ? answersRef.current : answers;
    const currentQuestions = auto ? questionsRef.current : questions;

    const payload = {
  answers: currentQuestions.map((q) => ({ questionId: q.id, answer: currentAnswers[q.id] || '' })),
  tabSwitchViolation: violation,
};

    try {
      setSubmitting(true);
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/assessment/${token}/submit`, payload);
      setSubmitted(true);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to submit assessment. Please try again.';
      alert(message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------------- RENDER STATES ---------------------- */

  if (loading) {
    return (
      <CenteredCard>
        <Loader size={32} style={{ color: '#2563eb' }} />
        <p style={{ marginTop: '12px', color: '#64748b' }}>Loading your assessment...</p>
      </CenteredCard>
    );
  }

  if (error) {
    return (
      <CenteredCard>
        <AlertCircle size={40} style={{ color: '#ef4444' }} />
        <h2 style={{ marginTop: '12px', color: '#1e293b' }}>Unable to Load Assessment</h2>
        <p style={{ color: '#64748b', marginTop: '4px' }}>{error}</p>
      </CenteredCard>
    );
  }

  if (submitted) {
    return (
      <CenteredCard>
        <CheckCircle size={48} style={{ color: '#16a34a' }} />
        <h2 style={{ marginTop: '12px', color: '#1e293b' }}>Assessment Submitted</h2>
        <p style={{ color: '#64748b', marginTop: '4px', maxWidth: '400px', textAlign: 'center' }}>
          {tabSwitchDetected
            ? 'Your assessment was automatically submitted because you switched tabs or windows.'
            : timeExpired
            ? 'Your assessment was automatically submitted because the time limit was reached.'
            : `Thank you${candidateName ? `, ${candidateName}` : ''}. Your responses have been recorded. You may now close this page.`}
        </p>
      </CenteredCard>
    );
  }

  /* ---------------------- MAIN FORM ---------------------- */

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', padding: 'clamp(16px, 4vw, 40px) 12px' }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 10, background: timeLeftSeconds <= 60 ? '#dc2626' : '#1e293b',
        color: '#fff', textAlign: 'center', padding: '8px', fontWeight: 700, fontSize: '0.95rem'
      }}>
        Time remaining: {formatTimeLeft(timeLeftSeconds)}
      </div>
            {showTabWarning && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50
        }}>
          <div style={{
            background: '#fff', borderRadius: '10px', padding: '28px',
            maxWidth: '380px', textAlign: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
          }}>
            <AlertCircle size={36} style={{ color: '#dc2626' }} />
            <h3 style={{ margin: '12px 0 8px', color: '#1e293b' }}>Stay on this page</h3>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '20px' }}>
              You have switched tabs or windows {tabSwitchCount} time(s). Please stay on this page during the assessment.
            </p>
            <button
              onClick={() => setShowTabWarning(false)}
              style={{
                padding: '10px 24px', borderRadius: '6px', border: 'none',
                background: '#2563eb', color: '#fff', fontWeight: 600,
                fontSize: '0.9rem', cursor: 'pointer'
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}
      <div style={{ maxWidth: '760px', margin: '0 auto' }}></div>
      <div style={{ maxWidth: '760px', margin: '0 auto', width: '100%' }}>
        <p style={{ color: '#dc2626', fontWeight: 700, textAlign: 'center', marginBottom: '12px', fontSize: 'clamp(0.8rem, 3vw, 1rem)' }}>
          Do not switch tabs or windows during this assessment. Tab switches are monitored.
        </p>
        <div style={{
          background: '#0f172a', borderRadius: '10px 10px 0 0',
          padding: '24px', color: '#fff'
        }}>
          <h2 style={{ margin: 0 }}>Candidate Assessment</h2>
          {candidateName && (
            <p style={{ margin: '6px 0 0', color: '#cbd5e1', fontSize: '0.9rem' }}>Hi {candidateName}, please answer all questions below.</p>
          )}
        </div>

        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderTop: 'none',
          borderRadius: '0 0 10px 10px', padding: '24px'
        }}>
          {questions.map((q, idx) => (
            <QuestionBlock
              key={q.id}
              index={idx + 1}
              question={q}
              value={answers[q.id] || ''}
              onChange={(val) => handleAnswerChange(q.id, val)}
            />
          ))}

          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
              {questions.length - unansweredCount} of {questions.length} answered
            </span>
            <button
              onClick={() => handleSubmit(false)}
              disabled={submitting}
              style={{
                padding: '10px 24px', borderRadius: '6px', border: 'none',
                background: submitting ? '#93c5fd' : '#2563eb',
                color: '#fff', fontWeight: 600, fontSize: '0.95rem',
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Assessment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------- SUB-COMPONENTS ---------------------- */

function CenteredCard({ children }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: '24px'
    }}>
      {children}
    </div>
  );
}

function QuestionBlock({ index, question, value, onChange }) {
  return (
    <div style={{ marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #f1f5f9' }}>
      <p style={{ fontWeight: 600, color: '#1e293b', marginBottom: '10px' }}>
        {index}. {question.question}
        <span style={{
          marginLeft: '8px', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase',
          color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px'
        }}>
          {question.type === 'mcq' ? 'Multiple Choice' : question.type === 'coding' ? 'Coding' : 'Short Answer'}
        </span>
      </p>

      {question.type === 'mcq' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {(question.options || []).map((opt) => (
            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                name={question.id}
                checked={value === opt}
                onChange={() => onChange(opt)}
              />
              <span style={{ color: '#334155' }}>{opt}</span>
            </label>
          ))}
        </div>
      )}

      {question.type === 'coding' && (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Write your code here..."
          rows={4}
          style={{
            width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0',
            fontFamily: 'monospace', fontSize: '0.9rem', resize: 'vertical'
          }}
        />
      )}

      {question.type === 'short_answer' && (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type your answer here..."
          rows={3}
          style={{
            width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0',
            fontSize: '0.9rem', resize: 'vertical'
          }}
        />
      )}
    </div>
  );
}